// Subscription Model and Helper Functions

export const SUBSCRIPTION_STATUSES = {
    TRIAL: 'trial',
    ACTIVE: 'active',
    EXPIRED: 'expired',
    CANCELLED: 'cancelled',
    PENDING: 'pending'
};

export const SUBSCRIPTION_PLANS = {
    TRIAL: 'trial',
    PREMIUM: 'premium'
};

export class Subscription {
    constructor(data) {
        if (!data) {
            console.warn('Subscription constructor: No data provided');
            data = {};
        }

        this.id = data.id || null;
        this.userId = data.userId || null;
        this.status = data.status || 'none';
        this.autoRenewal = data.autoRenewal !== undefined ? data.autoRenewal : true; // Auto-renewal status
        this.startDate = data.startDate ? new Date(data.startDate) : new Date();
        this.endDate = data.endDate ? new Date(data.endDate) : null;
        this.trialEndDate = data.trialEndDate ? new Date(data.trialEndDate) : null;
        this.planId = data.planId || 'basic';
        this.planDisplay = data.planDisplay || 'BASIC PLAN';
        this.statusDisplay = data.statusDisplay || 'BASIC';
        this.stripePriceId = data.stripePriceId || null; // The specific pricing tier (monthly, quarterly, yearly)
    }

    isTrial() {
        return false; // No more trial plans
    }

    isActive() {
        return ['basic', 'premium'].includes(this.status);
    }

    isExpired() {
        if (this.status === 'basic') return false; // Basic plan never expires
        if (this.status === 'premium' && this.endDate) {
            return new Date() > this.endDate;
        }
        return this.status === 'expired';
    }

    getPlanDisplay() {
        return this.planDisplay;
    }

    getStatusDisplay() {
        return this.statusDisplay;
    }

    getFormattedStartDate() {
        try {
            if (!this.startDate) return 'N/A';
            return this.startDate.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        } catch (error) {
            console.error('Error formatting start date:', error);
            return 'N/A';
        }
    }

    getFormattedEndDate() {
        try {
            if (!this.endDate) return 'N/A';
            return this.endDate.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        } catch (error) {
            console.error('Error formatting end date:', error);
            return 'N/A';
        }
    }

    getFormattedTrialEndDate() {
        return 'N/A'; // No more trial plans
    }

    getDaysRemaining() {
        try {
            if (this.status === 'basic') return null; // Basic plan doesn't expire
            if (this.status === 'premium' && this.endDate) {
                const now = new Date();
                const diffTime = this.endDate - now;
                return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            }
            return null;
        } catch (error) {
            console.error('Error calculating days remaining:', error);
            return null;
        }
    }

    getTrialDaysRemaining() {
        return null; // No more trial plans
    }

    // New method for Premium renewal notifications
    getDaysUntilRenewal() {
        try {
            if (this.status === 'premium' && this.endDate) {
                const now = new Date();
                const diffTime = this.endDate - now;
                return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            }
            return null;
        } catch (error) {
            console.error('Error calculating days until renewal:', error);
            return null;
        }
    }

    // Check if Premium plan needs renewal notification (1 week before)
    needsRenewalNotification() {
        try {
            if (this.status === 'premium' && this.endDate) {
                const daysUntilRenewal = this.getDaysUntilRenewal();
                return daysUntilRenewal !== null && daysUntilRenewal <= 7 && daysUntilRenewal > 0;
            }
            return false;
        } catch (error) {
            console.error('Error checking renewal notification:', error);
            return false;
        }
    }
}

// Helper function to create subscription from API data
export const createSubscriptionFromData = (data) => {
    // Add null check for data
    if (!data) {
        console.warn('createSubscriptionFromData: No data provided');
        return null;
    }
    
    try {
        return new Subscription(data);
    } catch (error) {
        console.error('Error creating subscription object:', error);
        return null;
    }
};

// Helper function to check if user needs subscription warning
export const checkSubscriptionWarnings = (subscription) => {
    if (!subscription) {
        return [];
    }
    
    const warnings = [];
    
    try {
        if (subscription.isExpired()) {
            warnings.push({
                type: 'expired',
                message: 'Your subscription has expired. Please renew to continue accessing premium features.',
                severity: 'error'
            });
        }
        
        // Check for Premium renewal notification (2-7 days before)
        // Exclude 1 day to avoid duplicate with the "tomorrow" notification
        const daysUntilRenewal = subscription.getDaysUntilRenewal();
        console.log('[checkSubscriptionWarnings] Days until renewal:', daysUntilRenewal);
        console.log('[checkSubscriptionWarnings] needsRenewalNotification:', subscription.needsRenewalNotification());

        if (subscription.needsRenewalNotification()) {
            if (daysUntilRenewal > 1) {
                console.log('[checkSubscriptionWarnings] Adding general renewal warning for', daysUntilRenewal, 'days');
                warnings.push({
                    type: 'renewal',
                    message: `Your Premium subscription will renew in ${daysUntilRenewal} days.`,
                    severity: 'info'
                });
            } else {
                console.log('[checkSubscriptionWarnings] Skipping general warning for day 1 to avoid duplicate');
            }
        }

        // Check if Premium plan is about to expire (1 day before)
        if (subscription.status === 'premium' && daysUntilRenewal === 1) {
            console.log('[checkSubscriptionWarnings] Adding tomorrow warning');
            warnings.push({
                type: 'renewal',
                message: 'Your Premium subscription will renew tomorrow.',
                severity: 'warning'
            });
        }
        
        // Check if Premium plan has expired
        if (subscription.status === 'premium' && subscription.isExpired()) {
            warnings.push({
                type: 'expired',
                message: 'Your Premium subscription has expired. You can upgrade again anytime.',
                severity: 'error'
            });
        }
        
    } catch (error) {
        console.error('Error checking subscription warnings:', error);
    }

    console.log('[checkSubscriptionWarnings] Final warnings array:', warnings);
    console.log('[checkSubscriptionWarnings] Total warnings count:', warnings.length);

    return warnings;
};
